import { useMemo, type ReactNode } from 'react';
import {
  Route,
  Routes,
  useNavigate,
  type NavigateFunction,
} from 'react-router-dom';

import { NavigationContext } from '@/hooks/useNavigationContext';

import { CaptureFlowProvider } from '@/james-playground/magic-capture/CaptureFlowContext';
import { at } from '@/james-playground/magic-capture/mount';
import PrototypeChrome from '@/james-playground/magic-capture/components/PrototypeChrome';
import EventsPanel from '@/james-playground/magic-capture/components/EventsPanel';
import HomeTab from '@/james-playground/magic-capture/HomeTab';
import CareTab from '@/james-playground/magic-capture/CareTab';
import ActionsTab from '@/james-playground/magic-capture/ActionsTab';
import HelpTab from '@/james-playground/magic-capture/HelpTab';
import CamView from '@/james-playground/magic-capture/CamView';
import SavedDocumentView from '@/james-playground/magic-capture/SavedDocumentView';
import Capture from '@/james-playground/magic-capture/Capture';
import Processing from '@/james-playground/magic-capture/Processing';
import Result from '@/james-playground/magic-capture/Result';
import ActionReview from '@/james-playground/magic-capture/ActionReview';

// Every screen in here navigates through useNavigation() with its own paths, so
// one provider is all it takes to put the prototype on the app's router: the
// paths get prefixed with wherever it is mounted on the way through.
const PrototypeNavigation = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const value = useMemo(
    () => ({
      navigate: ((to: string | number, options?: unknown) =>
        typeof to === 'number'
          ? navigate(to)
          : navigate(at(to), options as never)) as NavigateFunction,
    }),
    [navigate],
  );
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

// The magic-capture prototype as a routable page. Its routes are relative to its
// mount, so the journey walks the real address bar and a screen inside it can be
// linked to.
const MagicCapture = () => (
  <PrototypeNavigation>
    <CaptureFlowProvider>
      <Routes>
        <Route element={<PrototypeChrome />}>
          <Route index element={<HomeTab />} />
          <Route path='actions' element={<ActionsTab />} />
          <Route path='care' element={<CareTab />} />
          <Route path='help' element={<HelpTab />} />
          <Route path='cam' element={<CamView />} />
        </Route>
        <Route path='capture' element={<Capture />} />
        <Route path='capture/processing' element={<Processing />} />
        <Route path='capture/result' element={<Result />} />
        <Route path='capture/review' element={<ActionReview />} />
        <Route path='documents/:id' element={<SavedDocumentView />} />
      </Routes>
      <EventsPanel />
    </CaptureFlowProvider>
  </PrototypeNavigation>
);

export default MagicCapture;
